import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  groupPorts,
  hasNamedGroups,
  resolveRoute,
  type PortGroup,
  type RouterPort,
} from "./router-model";
import { RenamableLabel, useRename } from "./rename";
import { ROUTE_STATE_DOT, ROUTE_STATE_LABEL } from "./route-style";
import type { RouterViewProps } from "./types";

/* ─────────────────────────────────────────────────────────── */
/* Select View — one row per target with a searchable combobox  */
/* ─────────────────────────────────────────────────────────── */

export const RouterSelect = (props: RouterViewProps) => {
  const { sources, targets, sourceNoun, targetNoun } = props;
  const sourceGroups = useMemo(() => groupPorts(sources), [sources]);
  const targetGroups = useMemo(() => groupPorts(targets), [targets]);
  const showTargetGroups = hasNamedGroups(targetGroups);

  if (targets.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
        No {targetNoun} ports available.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {targetGroups.map((g) => (
              <div key={g.key} className="divide-y">
                {showTargetGroups && (
                  <div className="bg-muted/50 px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {g.label || "—"}
                  </div>
                )}
                {g.ports.map((target) => (
                  <SelectRow
                    key={target.id}
                    {...props}
                    target={target}
                    sourceGroups={sourceGroups}
                  />
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <SourceLabels groups={sourceGroups} sourceNoun={sourceNoun} />
    </div>
  );
};

interface SelectRowProps extends RouterViewProps {
  target: RouterPort;
  sourceGroups: PortGroup[];
}

const SelectRow = ({
  target,
  sourceGroups,
  routes,
  routeState,
  routeFallbackLabel,
  sourcesById,
  allowClear,
  sourceNoun,
  canRouteTarget,
  clickSource,
  clearRoute,
}: SelectRowProps) => {
  const [open, setOpen] = useState(false);
  const resolved = resolveRoute(
    target.id,
    routes,
    sourcesById,
    routeState,
    routeFallbackLabel,
  );
  const showGroupHeadings = hasNamedGroups(sourceGroups);
  const dot = resolved.state ? ROUTE_STATE_DOT[resolved.state] : null;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5">
      <RenamableLabel
        kind="target"
        port={target}
        className="flex-1 text-xs font-medium"
      />
      <span className="text-xs text-muted-foreground">←</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-[140px] justify-between px-2 text-xs font-normal sm:w-[200px]"
            disabled={!canRouteTarget(target)}
            data-testid={`router-select-${target.id}`}
            data-state-route={resolved.state}
            title={
              resolved.state && resolved.state !== "ok"
                ? `${ROUTE_STATE_LABEL[resolved.state]}: ${resolved.label}`
                : resolved.label || undefined
            }
          >
            <span className="flex min-w-0 items-center gap-1.5">
              {dot && (
                <span className={cn("h-2 w-2 shrink-0 rounded-full", dot)} />
              )}
              <span
                className={cn(
                  "truncate",
                  !resolved.source && "italic text-muted-foreground",
                )}
              >
                {resolved.label || "None"}
              </span>
            </span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="end">
          <Command>
            <CommandInput
              placeholder={`Search ${sourceNoun.toLowerCase()}...`}
              className="h-8 text-xs"
            />
            <CommandList className="max-h-[260px]">
              <CommandEmpty className="py-3 text-xs">
                No {sourceNoun.toLowerCase()} found.
              </CommandEmpty>
              {allowClear && (
                <>
                  <CommandGroup>
                    <CommandItem
                      value="__none__"
                      keywords={["none", "clear"]}
                      onSelect={() => {
                        clearRoute(target.id);
                        setOpen(false);
                      }}
                      className="text-xs italic text-muted-foreground"
                    >
                      <Check
                        className={cn(
                          "mr-1.5 h-3 w-3",
                          resolved.sourceId === null
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      None
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              {sourceGroups.map((g) => (
                <CommandGroup
                  key={g.key}
                  heading={showGroupHeadings ? g.label || "—" : undefined}
                >
                  {g.ports.map((source) => (
                    <CommandItem
                      key={source.id}
                      value={`${g.key}\u0000${source.id}`}
                      keywords={[source.label, g.label]}
                      disabled={source.disabled}
                      onSelect={() => {
                        if (source.id !== resolved.sourceId) {
                          clickSource(target.id, source.id);
                        }
                        setOpen(false);
                      }}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          "mr-1.5 h-3 w-3 shrink-0",
                          resolved.sourceId === source.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span className="truncate">{source.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Source labels — lets sources be renamed in the select view   */
/* ─────────────────────────────────────────────────────────── */

const SourceLabels = ({
  groups,
  sourceNoun,
}: {
  groups: PortGroup[];
  sourceNoun: string;
}) => {
  const rename = useRename();
  const [open, setOpen] = useState(false);
  const anyRenamable = groups.some((g) =>
    g.ports.some((p) => rename.canRename("source", p)),
  );
  if (!anyRenamable) return null;
  const showHeadings = hasNamedGroups(groups);
  const count = groups.reduce((n, g) => n + g.ports.length, 0);

  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
        data-testid="router-source-labels-toggle"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {sourceNoun} names ({count})
      </button>
      {open && (
        <Card className="mt-1.5">
          <CardContent className="space-y-2 p-3">
            <p className="text-[10px] text-muted-foreground">
              Double-click or right-click a name to rename it.
            </p>
            {groups.map((g) => (
              <div key={g.key}>
                {showHeadings && (
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {g.label || "—"}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
                  {g.ports.map((source) => (
                    <div
                      key={source.id}
                      className="min-w-0 rounded-sm border px-1.5 py-1 text-xs"
                    >
                      <RenamableLabel kind="source" port={source} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
